import { Box, Typography } from '@mui/material';

import { LoadingSpinner } from '@modules/_shared/components/LoadingSpinner';
import TransactionResultItem from '@modules/reports/cma-reports/step-transaction/TransactionResultItem';
import type {
  MergedTransactionRow,
  TransactionSource,
} from '@modules/reports/cma-reports/step-transaction/types';
import { encodeSelectionKey } from '@modules/reports/cma-reports/step-transaction/utils/selectionKey';
import { useLocale } from '@shared/hooks/locale';

type TransactionGridViewProps = {
  transactions: MergedTransactionRow[];
  selectedIds: Set<string>;
  onToggleSelect: (id: string, source: TransactionSource) => void;
  loading: boolean;
};

export const TransactionGridView = ({
  transactions,
  selectedIds,
  onToggleSelect,
  loading,
}: TransactionGridViewProps) => {
  const { t } = useLocale();

  if (loading) {
    return <LoadingSpinner role="status" aria-label={t('Loading...')} />;
  }

  if (transactions.length === 0) {
    return (
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          p: 2,
          textAlign: 'center',
        }}
      >
        {t('No transactions found')}
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, 1fr)',
          md: 'repeat(2, 1fr)',
          lg: 'repeat(3, 1fr)',
          xl: 'repeat(3, 1fr)',
        },
        gap: 2,
      }}
    >
      {transactions.map(transaction => {
        const selectionKey = encodeSelectionKey(
          transaction.source,
          transaction.transaction_id,
        );
        return (
          <TransactionResultItem
            key={selectionKey}
            transaction={transaction}
            selected={selectedIds.has(selectionKey)}
            onToggle={onToggleSelect}
          />
        );
      })}
    </Box>
  );
};
